import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDemoMode } from '../../context/DemoModeContext.js';
import { api } from '../../lib/api.js';
import { SourceBadge } from '../../components/ui/SourceBadge.js';

interface Scheme {
  id: string;
  schemeCode: string;
  schemeType: string;
  name: string;
  description: string;
  mechanic: Record<string, unknown>;
  isoCurrencyCode: string;
  validFrom: string;
  validTo: string;
  budgetCap: string | null;
  budgetCapAmount: string | null;
  budgetSpentAmount: string;
  budgetCurrency: string | null;
  status: string;
  sourceType: string;
}

interface SchemesResponse {
  items?: Scheme[];
}

export function AdminSchemesPage() {
  const { t } = useTranslation();
  const { persona } = useDemoMode();
  const navigate = useNavigate();
  const [schemes, setSchemes] = useState<Scheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get<SchemesResponse | Scheme[]>('/admin/schemes')
      .then((data) => {
        if (Array.isArray(data)) {
          setSchemes(data);
        } else {
          setSchemes((data as SchemesResponse).items ?? []);
        }
        setLoading(false);
      })
      .catch((e: Error) => { setError(e.message); setLoading(false); });
  }, [persona]);

  if (persona.role !== 'brand_admin') {
    return (
      <div className="max-w-2xl mx-auto py-8 text-slate-500 text-sm">
        {t('errors.forbidden')}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <span className="inline-block text-[11px] font-semibold tracking-[0.22em] uppercase text-copper mb-2">
            {t('admin.title')}
          </span>
          <h1 className="font-serif italic text-3xl text-ink">{t('admin.schemes.title')}</h1>
          <p className="mt-1 text-slate-500 text-sm">{t('admin.schemes.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/admin/schemes/upload')}
            className="px-4 py-2 border border-copper text-copper text-sm font-semibold rounded hover:bg-copper/5 transition-colors flex items-center gap-1.5"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            {t('admin.schemes.uploadMemo')}
          </button>
          <button
            onClick={() => navigate('/admin/schemes/new')}
            className="px-4 py-2 bg-copper text-white text-sm font-semibold rounded hover:bg-copper-deep transition-colors"
          >
            {t('admin.schemes.new')}
          </button>
        </div>
      </div>

      {loading && (
        <p className="text-slate-400 text-sm">{t('common.loading')}</p>
      )}
      {error && (
        <p className="text-danger text-sm">{error}</p>
      )}

      {!loading && !error && (
        <div className="border border-hairline rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bone border-b border-hairline">
              <tr>
                <th className="text-left px-4 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{t('admin.schemes.schemeCode')}</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{t('admin.schemes.name')}</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{t('admin.schemes.type')}</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{t('admin.schemes.sourceType')}</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{t('admin.schemes.validFrom')}</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{t('admin.schemes.validTo')}</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{t('admin.schemes.status')}</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{t('admin.schemes.budget')}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {schemes.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400 text-sm">{t('admin.schemes.noSchemes')}</td></tr>
              )}
              {schemes.map((s) => (
                <tr key={s.id} className="border-b border-hairline last:border-0 hover:bg-bone/50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-ink">{s.schemeCode}</td>
                  <td className="px-4 py-3 text-ink">{s.name}</td>
                  <td className="px-4 py-3 text-slate-600">{s.schemeType}</td>
                  <td className="px-4 py-3"><SourceBadge sourceType={s.sourceType} /></td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono">{new Date(s.validFrom).toLocaleDateString('id-ID')}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono">{new Date(s.validTo).toLocaleDateString('id-ID')}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${s.status === 'ACTIVE' ? 'bg-success/10 text-success' : 'bg-slate-100 text-slate-400'}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 min-w-[120px]">
                    {s.budgetCapAmount ? (() => {
                      const cap = Number(s.budgetCapAmount);
                      const spent = Number(s.budgetSpentAmount ?? 0);
                      const pct = cap > 0 ? Math.min(100, Math.round((spent / cap) * 100)) : 0;
                      const isOver = pct >= 90;
                      return (
                        <div className="space-y-0.5">
                          <div className="w-full bg-slate-100 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all ${isOver ? 'bg-danger' : 'bg-copper'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <p className="text-[10px] text-slate-500 font-mono">{pct}%</p>
                        </div>
                      );
                    })() : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => navigate(`/admin/schemes/${s.id}/edit`)}
                      className="text-xs text-copper hover:underline font-medium"
                    >
                      {t('admin.schemes.edit')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
